import { ArrowDownCircle, ArrowUpCircle, Clock3, Wallet } from "lucide-react";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import { Card } from "@/components/ui/card";
import { TradeType } from "@/interfaces/trade";
import { normalizeCurrency } from "@/utils";
import type { Advertisement } from "@/interfaces/advertisement";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setAmount,
  setSelectedAd,
  setSide,
} from "@/store/slices/p2pMarketSlice";
import { useState } from "react";
import TradeModal from "../TradeModal";
import { useMerchantAds, useSingleUser } from "@/hooks";
import { formatCurrency } from "@/utils/formatCurrency";

interface MerchantAdvertisementsProps {
  merchantId: string;
}

export default function MerchantAdvertisements({
  merchantId,
}: MerchantAdvertisementsProps) {
  const [showTradeModal, setShowTradeModal] = useState(false);

  const { data: ads } = useMerchantAds(merchantId);

  console.log("CHANT ADS", ads);

  const { data: merchant } = useSingleUser(merchantId);

  const dispatch = useAppDispatch();
  const { side, cryptoCode, selectedAd, amount } = useAppSelector(
    (state) => state.p2pMarket,
  );

  const handleStartTrade = (ad: Advertisement) => {
    dispatch(setSelectedAd(ad));
    dispatch(
      setSide(ad.type === TradeType.SELL ? TradeType.BUY : TradeType.SELL),
    );
    dispatch(setAmount(""));
    setShowTradeModal(true);
  };

  console.log("MERCHANT", merchant);

  if (!ads?.length) {
    return (
      <Card className="rounded-2xl">
        <div className="py-20 text-center">
          <h3 className="font-semibold text-lg">No Active Advertisements</h3>

          <p className="text-muted-foreground mt-2">
            This merchant currently has no active advertisements.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {ads.map((ad: any) => {
        const customerAction =
          ad.type === TradeType.SELL ? TradeType.BUY : TradeType.SELL;

        const isBuy = customerAction === TradeType.BUY;

        return (
          <Card
            key={ad.id}
            className="rounded-2xl transition-all duration-200 hover:shadow-lg"
          >
            <div className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                {/* Left */}

                <div className="space-y-5 flex-1">
                  <div className="flex items-center gap-3">
                    <Badge className={isBuy ? "bg-emerald-500" : "bg-red-500"}>
                      {isBuy ? (
                        <ArrowDownCircle className="mr-2 h-4 w-4" />
                      ) : (
                        <ArrowUpCircle className="mr-2 h-4 w-4" />
                      )}

                      {customerAction}
                    </Badge>

                    <h3 className="text-xl font-semibold">
                      {normalizeCurrency(ad.asset.currency)}
                    </h3>
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground">Price</p>

                    <h2 className="text-3xl font-bold">
                      {ad.fiatCurrency.iso} {Number(ad.price).toFixed(2)}
                    </h2>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-5">
                    <div>
                      <p className="text-sm text-muted-foreground">Available</p>

                      <p className="font-semibold">
                        {Number(ad?.available).toFixed(2)}{" "}
                        {normalizeCurrency(ad?.asset?.currency)}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground">Limit</p>

                      <p className="font-semibold">
                        {ad?.fiatCurrency?.iso} {ad?.minAmount * ad?.price}
                        {" - "}
                        {ad?.fiatCurrency?.iso} {ad?.maxAmount * ad?.price}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground">
                        Payment Window
                      </p>

                      <div className="flex items-center gap-2">
                        <Clock3 className="h-4 w-4" />

                        <span>{ad?.paymentWindow} mins</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Methods */}

                  <div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Payment Methods
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {ad?.paymentMethods?.map((method: any) => (
                        <Badge key={method.id} variant="outline">
                          {method?.type}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right */}

                <div className="flex flex-col gap-4 min-w-[220px]">
                  <Card className="bg-muted/40 border-0">
                    <div className="p-4">
                      <div className="flex items-center gap-2">
                        <Wallet className="h-5 w-5" />

                        <span className="font-medium">Total Value</span>
                      </div>

                      <h3 className="text-2xl font-bold mt-3">
                        {ad?.fiatCurrency?.iso}{" "}
                        {formatCurrency(ad.available * ad.price)}
                      </h3>
                    </div>
                  </Card>

                  <Button
                    onClick={() => handleStartTrade(ad)}
                    size="lg"
                    className={
                      isBuy
                        ? "bg-emerald-500 hover:bg-emerald-600"
                        : "bg-red-500 hover:bg-red-600"
                    }
                  >
                    {isBuy
                      ? `Buy ${normalizeCurrency(ad.asset.currency)}`
                      : `Sell ${normalizeCurrency(ad.asset.currency)}`}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        );
      })}

      {showTradeModal && selectedAd && (
        <TradeModal
          showTradeModal={showTradeModal}
          setShowTradeModal={setShowTradeModal}
          side={side}
          crypto={cryptoCode}
          selectedAd={selectedAd}
          amount={amount}
        />
      )}
    </div>
  );
}
